<template>
  <v-container class="teams-page py-6 py-md-10">
    <v-card class="hero-card mb-6" elevation="2">
      <v-card-text class="pa-6 pa-md-8">
        <div class="d-flex flex-column flex-sm-row align-sm-center">
          <div>
            <h1 class="garage-title primary--text mb-2">Garajul meu — Cluburi</h1>
            <p class="hero-copy mb-0">Toate cluburile în care ești implicat, organizate după rol și simulator.</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn v-if="availableSimulators.length > 0" color="blue" class="create-button mt-5 mt-sm-0" large depressed to="/teams/add">
            <v-icon left>mdi-plus</v-icon>Creează club
          </v-btn>
        </div>

        <div class="summary-row mt-7">
          <div v-for="item in summary" :key="item.label" class="summary-item">
            <span class="summary-value">{{ item.value }}</span>
            <span class="summary-label">{{ item.label }}</span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <div v-if="loading" class="loading-state py-12 text-center">
      <v-progress-circular indeterminate color="primary" size="42"></v-progress-circular>
      <div class="text-body-1 mt-4">Se încarcă cluburile…</div>
    </div>

    <v-row v-else>
      <v-col v-for="section in sections" :key="section.key" cols="12">
        <v-card class="section-card" outlined>
          <div class="section-heading d-flex align-center pa-5 pa-md-6">
            <v-avatar :color="section.color" size="44" class="mr-4"><v-icon dark>{{ section.icon }}</v-icon></v-avatar>
            <div>
              <h2 class="text-h6 font-weight-bold">{{ section.title }}</h2>
              <div class="text-body-2 secondary-text">{{ section.subtitle }}</div>
            </div>
            <v-spacer></v-spacer>
            <v-chip small label :color="section.color" dark>{{ section.total }}</v-chip>
          </div>
          <v-divider></v-divider>

          <div v-if="section.total" class="pa-4 pa-md-6">
            <div v-for="group in section.groups" :key="section.key + '-' + group.id" class="simulator-group">
              <div class="simulator-label d-flex align-center mb-3">
                <v-icon small color="grey" class="mr-2">mdi-steering</v-icon>
                <span class="text-overline font-weight-bold">{{ group.title }}</span>
                <span class="group-count ml-2">{{ group.teams.length }}</span>
              </div>

              <v-row dense>
                <v-col v-for="team in group.teams" :key="team._id" cols="12" md="6">
                  <v-card class="team-card fill-height" outlined>
                    <v-card-text class="d-flex align-start pa-4">
                      <v-avatar tile size="48" color="blue-grey darken-3" class="team-avatar mr-4">
                        <v-img v-if="teamLogoUrl(team)" :src="teamLogoUrl(team)" :alt="team.name"></v-img>
                        <span v-else class="white--text text-h6 font-weight-bold">{{ teamInitials(team.name) }}</span>
                      </v-avatar>
                      <div class="team-details min-width-0">
                        <div class="text-subtitle-1 font-weight-bold text-truncate">{{ team.name }}</div>
                        <div v-if="team.description" class="team-description text-body-2 mt-1">{{ team.description }}</div>
                        <div class="team-meta d-flex flex-wrap align-center mt-2">
                          <span><v-icon x-small class="mr-1">mdi-account-multiple</v-icon>{{ memberCount(team) }} {{ memberCount(team) === 1 ? 'pilot' : 'piloți' }}</span>
                          <span v-if="team.managers && team.managers.length" class="ml-3"><v-icon x-small class="mr-1">mdi-shield-account</v-icon>{{ team.managers.length }} {{ team.managers.length === 1 ? 'manager' : 'manageri' }}</span>
                        </div>
                      </div>
                      <v-tooltip v-if="section.editable" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn icon color="primary" class="edit-button ml-2" :aria-label="'Editează ' + team.name" v-bind="attrs" v-on="on" @click="editTeam(team._id)">
                            <v-icon>mdi-pencil-outline</v-icon>
                          </v-btn>
                        </template>
                        <span>Editează clubul</span>
                      </v-tooltip>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-else class="empty-state text-center pa-8">
            <v-icon size="42" color="grey lighten-1">{{ section.emptyIcon }}</v-icon>
            <div class="font-weight-medium mt-3">{{ section.emptyText }}</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="deleteDialog" persistent max-width="360">
      <v-card>
        <v-card-title class="headline">Ștergi clubul?</v-card-title>
        <v-card-text>Sigur vrei să ștergi clubul <strong>{{ teamToDelete.name }}</strong>? Acțiunea nu poate fi anulată.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Renunță</v-btn>
          <v-btn color="red darken-1" text @click="confirmTeamDeletion">Șterge</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

export default {
  data: () => ({ teamToDelete: {}, deleteDialog: false, loading: true }),
  computed: {
    ...mapState('team', ['teams', 'teamsUserCanDrive', 'teamsUserOwns', 'teamsUserCanManage', 'simulators', 'availableSimulators']),
    ...mapGetters('auth', ['userinfo']),
    summary() {
      return [
        { label: 'Deținute', value: this.teamsUserOwns.length },
        { label: 'Administrate', value: this.teamsUserCanManage.length },
        { label: 'Ca pilot', value: this.teamsUserCanDrive.length }
      ]
    },
    sections() {
      return [
        { key: 'owned', title: 'Cluburi pe care le dețin', subtitle: 'Ai control complet asupra acestor cluburi.', icon: 'mdi-crown-outline', color: 'amber darken-3', emptyIcon: 'mdi-crown-outline', emptyText: 'Nu deții încă niciun club.', editable: true, total: this.teamsUserOwns.length, groups: this.groupBySimulator(this.teamsUserOwns) },
        { key: 'managed', title: 'Cluburi pe care le administrez', subtitle: 'Cluburi în care poți gestiona piloții și invitațiile.', icon: 'mdi-shield-account-outline', color: 'blue darken-1', emptyIcon: 'mdi-shield-account-outline', emptyText: 'Nu administrezi niciun alt club.', editable: true, total: this.teamsUserCanManage.length, groups: this.groupBySimulator(this.teamsUserCanManage) },
        { key: 'driving', title: 'Cluburi în care sunt pilot', subtitle: 'Echipele alături de care concurezi.', icon: 'mdi-racing-helmet', color: 'deep-purple lighten-1', emptyIcon: 'mdi-racing-helmet', emptyText: 'Nu faci parte încă dintr-un club ca pilot.', editable: false, total: this.teamsUserCanDrive.length, groups: this.groupBySimulator(this.teamsUserCanDrive) }
      ]
    }
  },
  methods: {
    ...mapActions('team', ['getTeams', 'getTeamsUserCanDrive', 'getTeamsUserOwns', 'getTeamsUserCanManage', 'deleteTeam', 'getAvailableSimulators']),
    groupBySimulator(teams) {
      const groups = this.simulators.map(simulator => ({
        id: simulator._id,
        title: simulator.title,
        teams: teams.filter(team => team.simulator && team.simulator._id === simulator._id)
      })).filter(group => group.teams.length)
      const uncategorized = teams.filter(team => !team.simulator)
      if (uncategorized.length) groups.push({ id: 'other', title: 'Alte simulatoare', teams: uncategorized })
      return groups
    },
    teamInitials(name) {
      return (name || '?').split(/\s+/).slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase()
    },
    teamLogoUrl(team) {
      if (team.logo && team.logo.filename) {
        return `${process.env.VUE_APP_API_SCHEMA}${process.env.VUE_APP_API_HOST}/api/files/${team.logo.filename}`
      }
      return team.image || ''
    },
    memberCount(team) {
      return team.drivers ? team.drivers.length : 0
    },
    editTeam(id) {
      this.$router.push({ path: `/teams/edit/${id}` })
    },
    askDeleteTeamConfirmation(id) {
      this.teamToDelete = this.teams.find(team => team._id === id) || {}
      this.deleteDialog = true
    },
    async confirmTeamDeletion() {
      await this.deleteTeam(this.teamToDelete)
      await this.loadTeams()
      this.deleteDialog = false
    },
    async loadTeams() {
      await this.getTeams()
      await Promise.all([
        this.getTeamsUserCanDrive(this.userinfo.id),
        this.getTeamsUserOwns(this.userinfo.id),
        this.getTeamsUserCanManage(this.userinfo.id),
        this.getAvailableSimulators()
      ])
    }
  },
  async mounted() {
    try {
      await this.loadTeams()
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.teams-page { max-width: 1120px; }
.hero-card { overflow: hidden; border-radius: 4px; }
.garage-title { font-size: clamp(1.1rem, 2.2vw, 1.35rem); font-weight: 700; line-height: 1.4; letter-spacing: 0.1em; text-transform: uppercase; }
.hero-copy { max-width: 620px; opacity: 0.7; font-size: 1rem; }
.create-button { min-width: 168px; }
.summary-row { display: flex; flex-wrap: wrap; gap: 12px; }
.summary-item { display: flex; align-items: baseline; gap: 8px; min-width: 132px; padding: 10px 14px; border: 1px solid rgba(127, 127, 127, 0.25); border-radius: 4px; background: rgba(127, 127, 127, 0.06); }
.summary-value { font-size: 1.35rem; font-weight: 700; }
.summary-label { opacity: 0.68; font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.04em; }
.section-card { overflow: hidden; border-radius: 4px; }
.secondary-text, .team-meta, .team-description { color: #7b8490; }
.simulator-group + .simulator-group { margin-top: 28px; }
.simulator-label { color: #7b8490; }
.group-count { min-width: 22px; padding: 1px 7px; border-radius: 999px; background: rgba(127, 127, 127, 0.14); font-size: 0.72rem; text-align: center; }
.team-card { border-radius: 4px; transition: border-color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease; }
.team-card:hover { border-color: rgba(33, 150, 243, 0.55); box-shadow: 0 6px 18px rgba(14, 30, 45, 0.08); transform: translateY(-2px); }
.team-avatar, .edit-button { flex: 0 0 auto; }
.team-details { flex: 1 1 auto; }
.min-width-0 { min-width: 0; }
.team-description { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.team-meta { font-size: 0.76rem; }
.empty-state { color: #7b8490; }
@media (max-width: 599px) {
  .teams-page { padding-right: 12px; padding-left: 12px; }
  .create-button { width: 100%; }
  .summary-row { gap: 8px; }
  .summary-item { flex: 1 1 30%; min-width: 0; padding: 9px 10px; }
  .summary-label { font-size: 0.65rem; }
  .section-heading { align-items: flex-start !important; }
}
</style>
